import type { Attribution } from '@zetter/shared';

/**
 * Приём задачи с витрины: свой fetch, а не клиент API.
 *
 * По той же причине, что и в `public-data.ts`: `zetterApi` тянет за собой
 * контракты всего кабинета и zod, а витрине нужен один POST. Разница видна в
 * весе бандла, который грузит человек, пришедший из поиска, — то есть ровно
 * тот, ради кого первый экран и сделан.
 *
 * Адрес относительный: витрина и API живут на одном домене, и межоригинных
 * запросов здесь нет. Сессия не нужна вовсе — маршрут публичный.
 */
export interface DraftAccepted {
  token: string;
  expiresAt: string;
}

export interface DraftInput {
  kind: string;
  idea: string;
  /** Шаблон со страницы каталога. Пусто — документ соберётся с заготовки. */
  templateSlug?: string;
  /** Поле-ловушка: заполняет его робот, человек этого поля не видит. */
  trap: string;
  attribution?: Attribution | undefined;
}

/**
 * Ошибка, о которой человеку надо сказать словами.
 *
 * Отдельный класс, потому что у первого экрана два разных отказа: «слишком
 * часто» (можно подождать) и «что-то пошло не так» (можно повторить). Молча
 * проглотить нельзя ни тот, ни другой — человек только что написал текст и
 * ждёт, что с ним что-то произойдёт.
 */
export class DraftError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'DraftError';
  }
}

export async function postDraft(input: DraftInput): Promise<DraftAccepted> {
  let response: Response;
  try {
    response = await fetch('/api/public/drafts', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    throw new DraftError('Не удалось связаться с сервером. Проверьте соединение.', 0);
  }

  if (!response.ok) {
    // Сообщение сервера показывается как есть только для понятных отказов:
    // на остальное человеку нужен не код ошибки, а что делать дальше.
    const message =
      response.status === 429
        ? await serverMessage(response, 'Слишком много задач подряд. Попробуйте чуть позже.')
        : response.status === 422
          ? await serverMessage(response, 'Задача слишком длинная или пустая.')
          : 'Не получилось отправить задачу. Попробуйте ещё раз.';

    throw new DraftError(message, response.status);
  }

  const body = (await response.json()) as { data: DraftAccepted };
  return body.data;
}

async function serverMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message?.trim() || fallback;
  } catch {
    return fallback;
  }
}
